<template>
  <div
    v-click-outside="onClickOutside"
    :class="$style.special_paste"
    @click.stop
  >
    <div
      :class="$style.special_paste_popup"
    >
      <div :class="$style.special_paste_header">
        <span> {{ locale.title }} </span>
        <div :class="$style.header_icons">
          <span :class="$style.header_icons_info">
            <Tooltip
              :content="locale.info"
              :class="$style.tooltip"
              :position="tooltipPosition"
            >
              <template #body>
                <SvgSprite :name="'info'" />
              </template>
            </Tooltip>
          </span>
          <div :class="$style.header_icons_close">
            <icon-button
              :class="$style.icon"
              :icon="{ name:'close-1' }"
              size="small"
              @onClick="onClosePopup"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        :class="$style.loader_container"
      >
        <ve-progress
          :size="24"
          loading
          line="round"
          thickness="10%"
          empty-thickness="10%"
          color="#1565C0"
          empty-color="#73A3D94D"
        />
      </div>
      <template v-else>
        <div :class="$style.special_paste_selectAll">
          <VgpButton
            :type="'text'"
            :label="isAllSelected ? locale.unselectAll : locale.selectAll"
            small
            @onClick="onClickAll"
          />
        </div>

        <div
          :class="$style.special_paste_body"
          class="gantt_scroll"
        >
          <div
            v-for="item in options"
            :key="'special-option' + item.id"
            :class="$style.special_paste_option"
          >
            <VgpCheckbox
              :label="item.label"
              :label-position="'right'"
              :size="16"
              :checked="isSelected(item.id)"
              @update:checked="onSelectOption(item.id)"
            />
          </div>
        </div>
      </template>

      <div :class="$style.special_paste_footer">
        <VgpButton
          :class="$style.special_paste_footer__button"
          :type="'primary'"
          :label="locale.okBtn"
          :disabled="!hasChanges || isLoading"
          small
          @onClick="onClickOklBtn"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeProgress } from 'vue-ellipse-progress';
import axios from 'axios';
import Tooltip from '$$vueCmp/tooltips/tooltip.vue';
import app from '$$app';
import pricingHelper from '../../helpers/pricingHelper';
import { DEFAULT_OPTIONS } from './constants';
import iconButton from '../common/VueComponents/globalButton/withIcon/iconButton.vue';
import projectsModel from '$$projectModel';
import VueRouter from '../../router/main';

export default {
  name: 'CopySettingsPopup',
  components: { Tooltip, iconButton, VeProgress },
  data() {
    return {
      locales: __,
      events: [],
      locale: {
        title: __('special_paste_header'),
        info: __('special_paste_table_header_info'),
        cancelBtn: __('common_cancel'),
        okBtn: __('common_apply'),
        selectAll: __('multicombo_select_all_items'),
        unselectAll: __('multicombo_unselect_all_items'),
      },
      selected: [],
      hasChanges: false,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      copyData: 'copyPaste/copiedTask',
      customColumnIds: 'columns/getColumnIdsByProjectId',
      customColumnById: 'columns/getCustomColumnById',
      popupCoordinates: 'copyPaste/popupCoordinates',
      currentProjectId: 'copyPaste/currentProjectId',
    }),
    copySettingsConfig() {
      return this.$store.getters['copyPaste/copySettingsConfig'](this.currentProjectId);
    },
    customOptions() {
      const columns = [];

      if (!pricingHelper.checkPricingAccess('custom_fields')) { return columns; }

      const ganttId = VueRouter.currentRoute.value.params.projectId;
      const customColumnIds = this.customColumnIds(+ganttId);

      customColumnIds.forEach(columnId => {
        const column = this.customColumnById(columnId);

        if (column) {
          columns.push({
            label: column.name,
            id: +column.id,
          });
        }
      });

      return columns;
    },
    options() {
      return [...DEFAULT_OPTIONS, ...this.customOptions];
    },
    isAllSelected() {
      return this.selected.length === this.options.length;
    },
    selectedOptionsFromConfig() {
      let selectedOptions = [];

      if (this.copySettingsConfig) {
        selectedOptions = this.copySettingsConfig.settings.selected;
      } else {
        selectedOptions = this.options;
      }

      return selectedOptions;
    },
    tooltipPosition() {
      const popupCoordinates = this.popupCoordinates;
      let position = 'top';

      if (popupCoordinates?.left) {
        const clientWidth = document.documentElement.clientWidth;
        const diff = clientWidth - parseInt(this.popupCoordinates.left, 10);

        if ((diff / 2) < 180) {
          position = 'left';
        }
      }

      return position;
    },
  },

  watch: {
    selected: {
      handler(newData) {
        if (this.checkChanged(this.selectedOptionsFromConfig, newData)) {
          this.hasChanges = true;
        } else {
          this.hasChanges = false;
        }
      },
      deep: true,
    },
    copySettingsConfig() {
      this.selected = [...this.selectedOptionsFromConfig];
    },

  },

  created() {
    const e_1 = app.on('onAfterCollaboration', event => {
      if (event.event === 'CustomFieldFromProjectUnassigned'
                    && event.projects.find(projectId => projectId === projectsModel.getActiveGanttId())) {
        event.unassignedCustomFields.forEach(field => {
          const index = this.selected.indexOf(this.selected.find(id => id === field.customFieldId));

          if (index !== -1) this.selected.splice(index, 1);
        });
      }

      if (event.event === 'CustomFieldDeleted') {
        event.customFields.forEach(id => {
          const index = this.selected.indexOf(id);

          if (index !== -1) this.selected.splice(index, 1);
        });
      }

      if (event.event === 'ProjectDeleted') {
        if (this.isOpenCopyPopup) {
          const dataForCopy = this.copyData;

          if (event.projects.includes(dataForCopy.newGanttId)) {
            this.onClosePopup();
          }
        }
      }

      if (event.event === 'TaskDeleted') {
        if (this.isOpenCopyPopup) {
          const dataForCopy = this.copyData;
          const isTask = event.tasks.find(id => (id === dataForCopy.copyRootTaskId || dataForCopy.taskId));

          if (isTask) {
            this.onClosePopup();
          }
        }
      }
    });

    this.events.push(e_1);

    const e_2 = app.on('project:archive', (ganttId, isArchived) => {
      if (this.isOpenCopyPopup) {
        const dataForCopy = this.copyData;

        if (ganttId === dataForCopy.newGanttId) {
          this.onClosePopup();
        }
      }
    });

    this.events.push(e_2);

    if (!this.copySettingsConfig) {
      this.isLoading = true;
      setTimeout(() => {
        this.loadConfig();
      }, 300);
    }
  },

  mounted() {
    this.selected = [...this.selectedOptionsFromConfig];
  },

  beforeUnmount() {
    this.events?.forEach(id => app.off(id));
    this.events = [];
  },

  methods: {
    async loadConfig() {
      try {
        const res = await axios.get('/api/task/copy-settings', { params: { user_id: user.id, project_id: this.currentProjectId } });

        const specialPasteConfig = res.data;

        this.$store.commit('copyPaste/setCopySettingsConfig', specialPasteConfig);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    isSelected(name) {
      return this.selected.includes(name);
    },

    async onClickOklBtn() {
      await this.$store.dispatch('copyPaste/updateConfig', { projectId: this.currentProjectId, updated: { settings: { selected: this.selected } } });
      this.onClosePopup();
    },

    onClickAll() {
      this.selected = this.isAllSelected ? [] : [...this.options.map(i => i.id)];
    },

    onSelectOption(id) {
      const index = this.selected.findIndex(i => i === id);

      index < 0
        ? this.selected.push(id)
        : this.selected.splice(index, 1);
    },

    onClosePopup(e) {
      this.$store.commit('copyPaste/toggleCopySettingsPopup', false);
    },

    onClickOutside(e) {
      const popup = e.target.closest('.special_paste_popup');

      if (!popup) {
        this.onClosePopup();
      }
    },

    checkChanged(oldData, newData) {
      if (oldData.length !== newData.length) return true;
      const sortedOld = [...oldData].sort();
      const sortedNew = [...newData].sort();

      return !sortedOld.every((value, index) => value === sortedNew[index]);
    },
  },
};

</script>

<style module src="./popup.less" lang="less" />
